import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Save, Plus, Trash2, Info } from 'lucide-react';

const AboutSectionTab = ({ authAxios }) => {
  const [data, setData] = useState({
    subtitle: 'About Us',
    headline: 'Excellence in Property',
    headline_accent: 'Management',
    description_1: '',
    description_2: '',
    stats: []
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await authAxios.get('/admin/sections/about');
        if (res.data) {
          setData(prev => ({ ...prev, ...res.data }));
        }
      } catch (e) { console.error(e); }
    };
    fetch();
  }, [authAxios]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await authAxios.put('/admin/sections/about', data);
      toast.success('About section updated!');
    } catch (e) {
      toast.error('Failed to save');
    } finally { setSaving(false); }
  };

  const updateStat = (i, field, value) => {
    const stats = [...data.stats];
    stats[i] = { ...stats[i], [field]: field === 'number' ? parseInt(value) || 0 : value };
    setData({ ...data, stats });
  };

  const addStat = () => setData({ ...data, stats: [...data.stats, { number: 0, suffix: '', label: '' }] });
  const removeStat = (i) => setData({ ...data, stats: data.stats.filter((_, idx) => idx !== i) });

  return (
    <div className="max-w-2xl space-y-6" data-testid="about-section-tab">
      <div className="bg-[#111111] border border-white/5 p-6">
        <h2 className="font-mono font-bold mb-6 flex items-center gap-2">
          <Info className="w-5 h-5 text-[#D4AF37]" />
          About Section Content
        </h2>
        <div className="space-y-4">
          <div>
            <label className="text-white/50 text-xs font-mono uppercase block mb-2">Subtitle</label>
            <input value={data.subtitle} onChange={e => setData({...data, subtitle: e.target.value})} className="w-full bg-[#0A0A0A] border border-white/10 px-4 py-3 font-mono text-sm focus:border-[#D4AF37] outline-none" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-white/50 text-xs font-mono uppercase block mb-2">Headline</label>
              <input value={data.headline} onChange={e => setData({...data, headline: e.target.value})} className="w-full bg-[#0A0A0A] border border-white/10 px-4 py-3 font-mono text-sm focus:border-[#D4AF37] outline-none" />
            </div>
            <div>
              <label className="text-white/50 text-xs font-mono uppercase block mb-2">Headline Accent</label>
              <input value={data.headline_accent} onChange={e => setData({...data, headline_accent: e.target.value})} className="w-full bg-[#0A0A0A] border border-white/10 px-4 py-3 font-mono text-sm focus:border-[#D4AF37] outline-none" />
            </div>
          </div>
          <div>
            <label className="text-white/50 text-xs font-mono uppercase block mb-2">Description (Paragraph 1)</label>
            <textarea value={data.description_1} onChange={e => setData({...data, description_1: e.target.value})} rows={4} className="w-full bg-[#0A0A0A] border border-white/10 px-4 py-3 font-mono text-sm focus:border-[#D4AF37] outline-none resize-none" />
          </div>
          <div>
            <label className="text-white/50 text-xs font-mono uppercase block mb-2">Description (Paragraph 2)</label>
            <textarea value={data.description_2} onChange={e => setData({...data, description_2: e.target.value})} rows={4} className="w-full bg-[#0A0A0A] border border-white/10 px-4 py-3 font-mono text-sm focus:border-[#D4AF37] outline-none resize-none" />
          </div>
        </div>
      </div>

      <div className="bg-[#111111] border border-white/5 p-6">
        <h2 className="font-mono font-bold mb-6">Statistics</h2>
        <div className="space-y-3">
          {data.stats.map((stat, i) => (
            <div key={i} className="flex items-center gap-3 p-3 bg-[#0A0A0A] border border-white/5 rounded">
              <input type="number" value={stat.number} onChange={e => updateStat(i, 'number', e.target.value)} placeholder="100" className="w-20 bg-transparent border border-white/10 px-3 py-2 font-mono text-sm focus:border-[#D4AF37] outline-none text-center" />
              <input value={stat.suffix} onChange={e => updateStat(i, 'suffix', e.target.value)} placeholder="%" className="w-16 bg-transparent border border-white/10 px-3 py-2 font-mono text-sm focus:border-[#D4AF37] outline-none text-center" />
              <input value={stat.label} onChange={e => updateStat(i, 'label', e.target.value)} placeholder="Label" className="flex-1 bg-transparent border border-white/10 px-3 py-2 font-mono text-sm focus:border-[#D4AF37] outline-none" />
              <button onClick={() => removeStat(i)} className="p-2 text-red-400/60 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
          <button onClick={addStat} className="flex items-center gap-2 px-4 py-2.5 border border-dashed border-white/10 text-white/50 hover:text-white hover:border-white/30 transition-colors font-mono text-xs w-full justify-center">
            <Plus className="w-4 h-4" />Add Statistic
          </button>
        </div>
      </div>

      <button onClick={handleSave} disabled={saving} data-testid="save-about-section" className="flex items-center gap-2 px-6 py-3 bg-[#D4AF37] text-black font-mono text-sm hover:bg-[#D4AF37]/90 transition-colors disabled:opacity-50">
        <Save className="w-4 h-4" />{saving ? 'Saving...' : 'Save About Section'}
      </button>
    </div>
  );
};

export default AboutSectionTab;
